import React, { useEffect } from 'react';
import { Card, Form, Input, Select, Switch, Button, Space, Typography, App } from 'antd';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useDepartment, useDepartmentsForSelection, useCreateDepartment, useUpdateDepartment } from '@/hooks/useDepartments';
import type { CreateDepartmentPayload } from '@/services/department.service';
import { userService } from '@/services/user.service';

const { Title } = Typography;

function userLabel(u: { first_name: string; last_name?: string; email: string; username: string }): string {
  const name = [u.first_name, u.last_name].filter(Boolean).join(' ').trim();
  return name ? `${name} (${u.email})` : `${u.username} (${u.email})`;
}

const DepartmentFormPage: React.FC = () => {
  const { t } = useTranslation('finance');
  const { t: tc } = useTranslation('common');
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { message } = App.useApp();
  const [form] = Form.useForm();
  const isEdit = !!id;

  const { data: department, isLoading: loadingDept } = useDepartment(id);
  const { data: selectionList = [] } = useDepartmentsForSelection();
  const { data: users = [] } = useQuery({
    queryKey: ['users-for-department-process-owner'],
    queryFn: () => userService.getAll({ is_active: true }),
  });
  const createMutation = useCreateDepartment();
  const updateMutation = useUpdateDepartment();

  useEffect(() => {
    if (department) {
      form.setFieldsValue({
        department_code: department.department_code,
        department_name: department.department_name,
        parent_department_id: department.parent_department_id || undefined,
        department_head: department.department_head || undefined,
        cost_center_code: department.cost_center_code,
        description: department.description,
        is_active: department.is_active,
      });
    }
  }, [department, form]);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields() as CreateDepartmentPayload & { is_active?: boolean };
      if (isEdit && id) {
        await updateMutation.mutateAsync({ id, payload: values });
      } else {
        await createMutation.mutateAsync(values);
      }
      message.success(isEdit ? t('departmentUpdated') : t('departmentCreated'));
      navigate('/fin/departments');
    } catch (e) {
      if (e && typeof (e as any).errorFields !== 'undefined') return;
      message.error((e as Error)?.message || t('saveFailed'));
    }
  };

  if (isEdit && !department && !loadingDept) return null;
  if (isEdit && loadingDept) return <Card><p>{tc('loading')}</p></Card>;

  return (
    <Card>
      <Title level={4}>{isEdit ? t('editDepartment') : t('newDepartment')}</Title>
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        initialValues={{ is_active: true }}
        style={{ maxWidth: 480, marginTop: 16 }}
      >
        <Form.Item
          name="department_code"
          label={t('departmentCode')}
          rules={[{ required: true, message: t('fieldRequired') }]}
        >
          <Input placeholder={t('departmentCodePlaceholder')} disabled={isEdit} />
        </Form.Item>
        <Form.Item
          name="department_name"
          label={t('departmentName')}
          rules={[{ required: true, message: t('fieldRequired') }]}
        >
          <Input placeholder={t('departmentNamePlaceholder')} />
        </Form.Item>
        <Form.Item name="parent_department_id" label={t('parentDepartment')}>
          <Select
            allowClear
            placeholder={t('selectParent')}
            options={selectionList.filter((d) => d.id !== id).map((d) => ({ value: d.id, label: `${d.department_code} - ${d.department_name}` }))}
          />
        </Form.Item>
        <Form.Item
          name="department_head"
          label={t('processOwner')}
          extra={t('processOwnerExtra')}
        >
          <Select
            allowClear
            showSearch
            placeholder={t('selectUser')}
            optionFilterProp="label"
            options={users.map((u) => ({ value: u.id, label: userLabel(u) }))}
          />
        </Form.Item>
        <Form.Item name="cost_center_code" label={t('costCenterCode')}>
          <Input placeholder={t('optional')} />
        </Form.Item>
        <Form.Item name="description" label={t('description')}>
          <Input.TextArea rows={2} placeholder={t('optional')} />
        </Form.Item>
        <Form.Item name="is_active" label={t('active')} valuePropName="checked">
          <Switch />
        </Form.Item>
        <Space>
          <Button type="primary" htmlType="submit" loading={createMutation.isPending || updateMutation.isPending}>
            {isEdit ? t('update') : tc('create')}
          </Button>
          <Button onClick={() => navigate('/fin/departments')}>{tc('cancel')}</Button>
        </Space>
      </Form>
    </Card>
  );
};

export default DepartmentFormPage;
